import {
  GoogleAnalyticsService,
  AnalyticsEventAction,
  AnalyticsEventCategory,
  CustomAnalyticsEvent
} from '@/services/google/google-analytics.services';
import {
  SelectableVariantMenuItem,
  SelectableMenuItem
} from '@/models';
import kebabCase from 'lodash/kebabCase';

export interface MealBuilderAnalyticService {
  trackItemAnalytics(
    title: string,
    item: SelectableMenuItem | null,
    variant: SelectableVariantMenuItem | null | undefined,
    action: 'add' | 'remove' | 'portion' | 'customize',
    actionValue?: string,
  ): void;

  trackOptOutAnalytics(
    title: string,
    product: SelectableVariantMenuItem | null,
    action: 'add' | 'remove'
  ): void;
}

export class AppMealBuilderAnalyticService implements MealBuilderAnalyticService {
  private googleAnalytics: GoogleAnalyticsService;

  constructor(
    googleAnalytics: GoogleAnalyticsService
  ) {
    this.googleAnalytics = googleAnalytics;
  }

  trackItemAnalytics(
    title: string,
    item: SelectableMenuItem | null,
    variant: SelectableVariantMenuItem | null | undefined,
    action: 'add' | 'remove' | 'portion' | 'customize',
    actionValue?: string,
  ): void {
    const data = this.createEventData(variant, item, action, actionValue);
    const metric = this.newCustomEvent(title, data);
    this.googleAnalytics.sendCustomEvent(metric);
  }

  trackOptOutAnalytics(
    title: string,
    product: SelectableVariantMenuItem | null,
    action: 'add' | 'remove'
  ): void {
    const section = kebabCase(title);
    const itemName = kebabCase(`no-${section}`);

    const data: { [key: string]: string } = {
      builder: kebabCase(this.googleAnalytics.lastVisitedProductDetailView),
      product: kebabCase(product?.item?.itemName),
      'product-id': product?.item?.itemId ?? '',
      'item-name': kebabCase(itemName),
      'item-action': action,
    };

    const metric = this.newCustomEvent(title, data);
    this.googleAnalytics.sendCustomEvent(metric);
  }

  private createEventData(
    variant: SelectableVariantMenuItem | null | undefined,
    item: SelectableMenuItem | null,
    action: 'add' | 'remove' | 'portion' | 'customize',
    actionValue?: string
  ): { [key: string]: string } {
    const product = variant?.item;
    const productName = product?.itemName ?? '';
    const productId = product?.itemId ?? '';
    const menuItem = item?.item;

    const data: { [key: string]: string } = {
      builder: kebabCase(this.googleAnalytics.lastVisitedProductDetailView),
      product: kebabCase(productName),
      'product-id': productId,
      'item-name': kebabCase(menuItem?.itemName ?? productName),
      'item-id': menuItem?.itemId ?? productId,
      'item-action': action,
    };

    if (action === 'portion') {
      data['item-portion'] = kebabCase(actionValue);
    } else if (action === 'customize') {
      data['item-customize'] = kebabCase(actionValue);
    }

    return data;
  }

  private newCustomEvent(title: string, data: { [key: string]: string }): CustomAnalyticsEvent {
    const metric: CustomAnalyticsEvent = {
      category: AnalyticsEventCategory.MealBuilder,
      action: kebabCase(`${AnalyticsEventAction.Click}-${kebabCase(title)}`),
      values: data,
    };

    return metric;
  }
}
